'use client';

/**
 * Conveyor startup picker — the "what am I working on?" gate.
 *
 * On Beast load, if the conveyor isn't running, Boss picks the projects to put
 * on the ACTIVE ROSTER (capped, default 5). Everything else can be ordered into
 * a BACKLOG: when a roster project reports DONE, the next backlog project is
 * promoted into the freed slot automatically — that's the 24/7 rotation.
 *
 * Projects NOT picked are untouched by the conveyor, so manual work on any
 * session always stays possible.
 *
 * Roster state lives in the gateway (/conveyor/pool), not here — this is just
 * the picker. Dismissing without starting leaves the conveyor stopped.
 */
import { useCallback, useEffect, useState } from 'react';
import { useDragResize } from './useDragResize';

interface SessionRow { slug: string; name?: string; state?: string }

/** Per-window flag: has the roster gate been shown in THIS Beast load? */
const SEEN_KEY = 'conveyor:startup-shown';

export default function ConveyorStartup() {
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [pool, setPool] = useState<string[]>([]);
  const [queue, setQueue] = useState<string[]>([]);
  const [cap, setCap] = useState(5);
  const [busy, setBusy] = useState(false);
  // Movable/resizable: with 13+ projects the fixed-height list forced scrolling
  // in a box Boss couldn't enlarge or shift off the chat behind it.
  const { style, dragHandleProps, resizeHandleProps, reset } = useDragResize({
    key: 'startup', defaultW: 640, defaultH: 560, anchor: 'center', minW: 380, minH: 300,
  });

  useEffect(() => {
    (async () => {
      try {
        const [st, ss] = await Promise.all([
          fetch('/api/gw/conveyor/pool').then((r) => r.json()),
          fetch('/api/gw/sessions').then((r) => r.json()),
        ]);
        if (st?.cap) setCap(st.cap);
        setPool(Array.isArray(st?.pool) ? st.pool : []);
        setQueue(Array.isArray(st?.queue) ? st.queue : []);
        setSessions(Array.isArray(ss?.sessions) ? ss.sessions : []);
        // INTERRUPT ONLY WHEN THERE IS SOMETHING TO DECIDE. (Boss: "conveyor is
        // very invasive", 2026-08-20.)
        //
        // History: the rule was once `if (!running)`, which let a roster left
        // running by a PREVIOUS Beast process silently drive projects Boss never
        // picked. The fix — always prompt on a fresh window — overcorrected: the
        // panel now blocked the app on every new tab and every dev reload even
        // when running=false and nothing whatsoever was happening.
        //
        // The danger the gate exists for is precisely: WORK IS BEING AUTO-DRIVEN
        // AND BOSS HASN'T SEEN THE ROSTER IN THIS WINDOW. So gate on exactly
        // that. If nothing is running, no work is being driven, nothing can
        // happen behind his back — the corner badge is sufficient and the app
        // stays out of his way.
        const running = st?.running === true;
        const roster = Array.isArray(st?.pool) ? st.pool : [];
        if (running && roster.length > 0 && !sessionStorage.getItem(SEEN_KEY)) {
          sessionStorage.setItem(SEEN_KEY, '1');
          setOpen(true);
        }
      } catch { /* gateway down — stay closed rather than block the UI */ }
    })();
  }, []);

  // Escape always dismisses. A panel that can appear over your work must be
  // closable without hunting for a button.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const inPool = (s: string) => pool.includes(s);
  const inQueue = (s: string) => queue.includes(s);

  // pool → queue → off, so one control cycles a project through all three states
  const cycle = useCallback((slug: string) => {
    if (inPool(slug)) {
      setPool((p) => p.filter((x) => x !== slug));
      setQueue((q) => [...q, slug]);
    } else if (inQueue(slug)) {
      setQueue((q) => q.filter((x) => x !== slug));
    } else if (pool.length < cap) {
      setPool((p) => [...p, slug]);
    } else {
      setQueue((q) => [...q, slug]); // roster full → it goes to the backlog
    }
  }, [pool, queue, cap]);

  const start = async () => {
    setBusy(true);
    try {
      await fetch('/api/gw/conveyor/pool', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pool, queue, running: true }),
      });
      setOpen(false);
    } catch { /* surfaced by the queue popup */ }
    setBusy(false);
  };

  const stop = async () => {
    setBusy(true);
    try { await fetch('/api/gw/conveyor/stop', { method: 'POST' }); } catch {}
    setBusy(false);
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        title="Conveyor roster"
        className="fixed bottom-4 left-4 z-[90] flex items-center gap-2 rounded-full bg-zinc-800/90 hover:bg-zinc-700 border border-amber-500/50 px-3 py-2 text-sm shadow-lg"
      >
        <span>🏭</span>
        <span className="text-xs text-amber-300">
          {pool.length ? `${pool.length} active` : 'pick projects'}
        </span>
      </button>
    );
  }

  if (!style) return null;   // geometry not measured yet

  return (
    // No dimming backdrop. It was already pointer-events-none (clicks passed
    // through), so the black/50 + blur bought nothing functional while making a
    // panel Boss could otherwise ignore feel like a modal that had seized the
    // whole app. It is a floating panel now — movable, resizable, dismissible,
    // and it never obscures what is behind it.
    <div className="fixed inset-0 z-[110] pointer-events-none">
      <div
        data-conveyor-panel="startup"
        className="fixed overflow-hidden flex flex-col rounded-xl border border-amber-500/40 bg-zinc-900 shadow-2xl text-zinc-100 pointer-events-auto"
        style={style}
      >
        <div data-conveyor-drag className="p-4 border-b border-zinc-800 select-none" {...dragHandleProps}>
          <div className="flex items-start justify-between">
            <div className="text-lg font-semibold text-amber-400">🏭 Conveyor — pick today’s projects</div>
            <button
              data-no-drag
              onClick={reset}
              title="Reset position and size"
              className="rounded px-1.5 py-0.5 text-xs text-zinc-500 hover:text-zinc-200 hover:bg-zinc-700/60"
            >
              ↺
            </button>
          </div>
          <div className="mt-1 text-xs text-zinc-400">
            Up to <b>{cap}</b> run automatically and rotate tasks until finished. When one finishes,
            the next from the backlog takes its slot. Anything unpicked stays fully manual.
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {sessions.map((s) => {
            const active = inPool(s.slug);
            const queued = inQueue(s.slug);
            const qi = queue.indexOf(s.slug);
            return (
              <button
                key={s.slug}
                onClick={() => cycle(s.slug)}
                className={`w-full flex items-center justify-between rounded-md px-3 py-2 text-left text-sm border transition
                  ${active ? 'bg-emerald-600/20 border-emerald-500/60'
                    : queued ? 'bg-amber-600/10 border-amber-500/40'
                    : 'bg-zinc-800/40 border-zinc-700 hover:border-zinc-500'}`}
              >
                <span className="truncate">
                  <span className="mr-2">{active ? '▶️' : queued ? '⏳' : '○'}</span>
                  {s.name || s.slug}
                  <span className="ml-2 text-xs text-zinc-500">({s.slug})</span>
                </span>
                <span className="text-xs text-zinc-400 shrink-0 ml-2">
                  {active ? 'ACTIVE' : queued ? `backlog #${qi + 1}` : s.state === 'working' ? 'working (manual)' : ''}
                </span>
              </button>
            );
          })}
          {sessions.length === 0 && <div className="text-center text-sm text-zinc-500 py-6">No sessions found.</div>}
        </div>

        <div className="p-4 border-t border-zinc-800 flex items-center gap-2">
          <div className="text-xs text-zinc-400 flex-1">
            <b className="text-emerald-400">{pool.length}</b>/{cap} active · <b className="text-amber-400">{queue.length}</b> queued
          </div>
          <button onClick={stop} disabled={busy} className="rounded-md bg-zinc-700 hover:bg-zinc-600 px-3 py-1.5 text-sm disabled:opacity-50">
            Stop conveyor
          </button>
          <button onClick={() => setOpen(false)} className="rounded-md bg-zinc-700 hover:bg-zinc-600 px-3 py-1.5 text-sm">
            Later
          </button>
          <button
            onClick={start}
            disabled={busy || pool.length === 0}
            className="rounded-md bg-emerald-600 hover:bg-emerald-500 px-4 py-1.5 text-sm font-medium disabled:opacity-50"
          >
            ▶️ Start 24/7
          </button>
        </div>
        <div
          data-conveyor-resize
          {...resizeHandleProps}
          className="absolute bottom-0 right-0 h-4 w-4 border-r-2 border-b-2 border-amber-500/50 hover:border-amber-400 rounded-br-xl"
        />
      </div>
    </div>
  );
}
